import React from 'react';
import { Dialog, DialogContent } from './dialog';
import { X } from 'lucide-react';

/**
 * JewelModal - Premium "Jewel & Luxury" modal shell
 * Consistent glass-morphism, gradient ring, hero icon header, ambient glow, animated close.
 *
 * Props:
 *  open, onOpenChange
 *  icon: Lucide icon component
 *  title, subtitle
 *  accent: 'cyan' | 'violet' | 'emerald' | 'amber' | 'rose' | 'teal' | 'pink' | 'blue'  (default 'cyan')
 *  size: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
 *  hideClose: boolean
 *  footer: ReactNode  (rendered in sticky-ish footer bar)
 *  children: main body
 */
export const ACCENT_TOKENS = {
  cyan: { grad: 'from-[#00E5FF] to-[#00B8D4]', ring: 'rgba(0,229,255,0.32)', bg: 'rgba(0,229,255,0.10)', chip: 'text-cyan-300 bg-cyan-500/10 border-cyan-400/25' },
  violet: { grad: 'from-[#8A2BE2] to-[#6a11cb]', ring: 'rgba(138,43,226,0.32)', bg: 'rgba(138,43,226,0.10)', chip: 'text-[#a673e8] bg-[#8A2BE2]/10 border-[#8A2BE2]/25' },
  emerald: { grad: 'from-emerald-400 to-teal-500', ring: 'rgba(52,211,153,0.32)', bg: 'rgba(52,211,153,0.10)', chip: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/25' },
  amber: { grad: 'from-amber-400 to-orange-500', ring: 'rgba(251,191,36,0.32)', bg: 'rgba(251,191,36,0.10)', chip: 'text-amber-300 bg-amber-500/10 border-amber-400/25' },
  rose: { grad: 'from-rose-400 to-pink-500', ring: 'rgba(244,63,94,0.32)', bg: 'rgba(244,63,94,0.10)', chip: 'text-rose-300 bg-rose-500/10 border-rose-400/25' },
  teal: { grad: 'from-teal-400 to-cyan-500', ring: 'rgba(45,212,191,0.32)', bg: 'rgba(45,212,191,0.10)', chip: 'text-teal-300 bg-teal-500/10 border-teal-400/25' },
  pink: { grad: 'from-pink-400 to-fuchsia-500', ring: 'rgba(236,72,153,0.32)', bg: 'rgba(236,72,153,0.10)', chip: 'text-pink-300 bg-pink-500/10 border-pink-400/25' },
  blue: { grad: 'from-sky-400 to-indigo-500', ring: 'rgba(56,189,248,0.32)', bg: 'rgba(56,189,248,0.10)', chip: 'text-sky-300 bg-sky-500/10 border-sky-400/25' },
  slate: { grad: 'from-slate-400 to-slate-500', ring: 'rgba(148,163,184,0.28)', bg: 'rgba(148,163,184,0.10)', chip: 'text-slate-300 bg-slate-500/10 border-slate-400/25' },
};

const SIZE_MAP = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
};

export const JewelModal = ({
  open, onOpenChange, icon: Icon, title, subtitle, accent = 'cyan', size = 'md',
  hideClose = false, footer, children, testid, headerRight,
}) => {
  const tokens = ACCENT_TOKENS[accent] || ACCENT_TOKENS.cyan;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testid}
        className={`p-0 gap-0 border-0 bg-transparent shadow-none max-w-[calc(100vw-2rem)] ${SIZE_MAP[size] || SIZE_MAP.md}`}
      >
        {/* Outer luxury shell */}
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.08]"
          style={{
            background: 'linear-gradient(180deg, rgba(15,15,22,0.96) 0%, rgba(5,5,10,0.98) 100%)',
            boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 40px -8px ${tokens.ring}, inset 0 1px 0 rgba(255,255,255,0.04)`,
          }}>
          {/* Ambient gradient glow */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -top-24 -left-16 w-72 h-72 rounded-full blur-[100px] opacity-40"
              style={{ background: `radial-gradient(circle, ${tokens.ring} 0%, transparent 70%)` }} />
            <div className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full blur-[100px] opacity-30"
              style={{ background: 'radial-gradient(circle, rgba(138,43,226,0.28) 0%, transparent 70%)' }} />
            {/* Grain */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
              backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' /></filter><rect width='100%' height='100%' filter='url(%23n)' /></svg>\")",
            }} />
          </div>

          {/* Top hairline */}
          <div className="absolute inset-x-0 top-0 h-px"
            style={{ background: `linear-gradient(90deg, transparent 0%, ${tokens.ring} 50%, transparent 100%)` }} />

          {/* Header */}
          <div className="relative px-6 pt-6 pb-4 flex items-start gap-4">
            {Icon && (
              <div className="relative w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${tokens.ring}, rgba(138,43,226,0.18))`,
                  boxShadow: `0 0 24px -4px ${tokens.ring}, inset 0 1px 0 rgba(255,255,255,0.08)`,
                }}>
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${tokens.grad} opacity-20`} />
                <Icon className="w-5 h-5 text-white relative z-10" strokeWidth={1.5} />
              </div>
            )}
            <div className="flex-1 min-w-0 pt-0.5">
              {title && (
                <h2 className="text-white text-xl font-light tracking-tight leading-tight">
                  {typeof title === 'string' && title.includes(' ') ? (
                    (() => {
                      const parts = title.split(' ');
                      const last = parts.pop();
                      return (<>
                        {parts.join(' ')}{' '}
                        <span className={`bg-gradient-to-r ${tokens.grad} bg-clip-text text-transparent font-medium`}>{last}</span>
                      </>);
                    })()
                  ) : title}
                </h2>
              )}
              {subtitle && (
                <p className="text-white/40 text-[11px] mt-1 tracking-[0.05em]">{subtitle}</p>
              )}
            </div>
            {headerRight}
            {!hideClose && (
              <button
                onClick={() => onOpenChange && onOpenChange(false)}
                className="w-9 h-9 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.06] transition-all group flex-shrink-0"
                data-testid="jewel-modal-close"
              >
                <X className="w-4 h-4 group-hover:rotate-90 transition-transform" />
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="relative mx-6 h-px bg-white/[0.05] mb-4" />

          {/* Body */}
          <div className="relative px-6 pb-6 max-h-[70vh] overflow-y-auto jewel-scroll">
            {children}
          </div>

          {footer && (
            <div className="relative border-t border-white/[0.05] px-6 py-4 bg-black/40 backdrop-blur-sm flex items-center justify-end gap-3">
              {footer}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ─────────────  FORM PRIMITIVES  ───────────── */

export const JewelField = ({ label, hint, children, error, required, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    {label && (
      <label className="flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase text-white/50 font-medium">
        {label}
        {required && <span className="text-rose-400 text-[10px]">*</span>}
      </label>
    )}
    {children}
    {hint && <p className="text-white/30 text-[10px]">{hint}</p>}
    {error && <p className="text-rose-400 text-[10px] flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rose-400" />{error}</p>}
  </div>
);

const baseInputCn = "w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.05] transition-all";

export const JewelInput = React.forwardRef(({ className = '', icon: Icon, ...props }, ref) => (
  <div className="relative">
    {Icon && (
      <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
    )}
    <input
      ref={ref}
      className={`${baseInputCn} ${Icon ? 'pl-11' : ''} ${className}`}
      {...props}
    />
  </div>
));
JewelInput.displayName = 'JewelInput';

export const JewelTextarea = React.forwardRef(({ className = '', rows = 3, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={`${baseInputCn} resize-none ${className}`}
    {...props}
  />
));
JewelTextarea.displayName = 'JewelTextarea';

export const JewelSelect = React.forwardRef(({ className = '', children, ...props }, ref) => {
  // Inject dark theme styling into every <option> so the native popup is readable on Jewel dark UI
  const styledChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    if (child.type === 'option' || child.type === 'optgroup') {
      return React.cloneElement(child, {
        style: { backgroundColor: '#0a0a14', color: '#ffffff', ...(child.props.style || {}) },
      });
    }
    return child;
  });
  return (
    <div className="relative">
      <select
        ref={ref}
        className={`${baseInputCn} appearance-none pr-10 cursor-pointer jewel-select ${className}`}
        {...props}
      >
        {styledChildren}
      </select>
      <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
});
JewelSelect.displayName = 'JewelSelect';

export const JewelChipRow = ({ options, value, onChange, accent = 'cyan', testidPrefix }) => {
  const tokens = ACCENT_TOKENS[accent] || ACCENT_TOKENS.cyan;
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={testidPrefix ? `${testidPrefix}-${opt.value}` : undefined}
            onClick={() => onChange(opt.value)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[11px] tracking-[0.05em] font-medium transition-all ${
              active
                ? `text-white border-white/20 shadow-[0_0_12px_rgba(0,229,255,0.15)]`
                : 'bg-white/[0.02] text-white/50 border-white/[0.06] hover:text-white/80 hover:border-white/[0.12]'
            }`}
            style={active ? { background: `linear-gradient(135deg, ${tokens.bg}, rgba(138,43,226,0.08))`, borderColor: tokens.ring } : {}}
          >
            {opt.icon && <opt.icon className="w-3.5 h-3.5" style={active ? { color: tokens.ring.replace('0.32', '1') } : {}} />}
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * JewelButton - luxury pill CTA
 * variant: 'primary' | 'ghost' | 'danger'
 */
export const JewelButton = React.forwardRef(({
  variant = 'primary', accent = 'cyan', className = '', icon: Icon, iconRight: IconRight, loading, children, ...props
}, ref) => {
  const tokens = ACCENT_TOKENS[accent] || ACCENT_TOKENS.cyan;
  let cn = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-medium tracking-[0.05em] transition-all disabled:opacity-40 disabled:cursor-not-allowed';
  if (variant === 'primary') {
    cn += ' text-black hover:brightness-110 active:brightness-95';
  } else if (variant === 'ghost') {
    cn += ' text-white/70 hover:text-white bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.06]';
  } else if (variant === 'danger') {
    cn += ' text-white bg-rose-500/20 border border-rose-500/40 hover:bg-rose-500/30';
  }
  const style = variant === 'primary'
    ? { background: `linear-gradient(135deg, ${tokens.ring.replace('0.32', '1')}, ${tokens.ring.replace('0.32', '0.75')})`, boxShadow: `0 8px 24px -6px ${tokens.ring}` }
    : {};
  return (
    <button ref={ref} className={`${cn} ${className}`} style={style} {...props}>
      {loading ? (
        <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
      ) : Icon ? <Icon className="w-3.5 h-3.5" /> : null}
      {children}
      {IconRight && <IconRight className="w-3.5 h-3.5" />}
    </button>
  );
});
JewelButton.displayName = 'JewelButton';

export default JewelModal;
